import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, Messaging, MessagePayload } from 'firebase/messaging';
import { getAnalytics, Analytics } from 'firebase/analytics';
import api from '@/lib/api';
import axios from 'axios';
import { getCookie } from 'cookies-next';
import { toast } from 'react-toastify';
import React, { createElement } from 'react';

// Firebase configuration interface
export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
  vapidKey?: string;
}

// Cache for Firebase config
let cachedConfig: FirebaseConfig | null = null;
let configPromise: Promise<FirebaseConfig> | null = null;

// Get Firebase configuration from API route
export const getFirebaseConfig = async (): Promise<FirebaseConfig> => {
  // Skip API call if on home page
  if (typeof window !== 'undefined') {
    const pathname = window.location.pathname;
    const isHomePage = pathname === '/' || pathname === '/home';
    
    if (isHomePage) {
      console.log('[Firebase Config] Skipping API call on home page');
      // Return empty config for home page
      return {
        apiKey: '',
        authDomain: '',
        projectId: '',
        storageBucket: '',
        messagingSenderId: '',
        appId: '',
        vapidKey: '',
      };
    }
  }

  // Return cached config if available
  if (cachedConfig) {
    return cachedConfig;
  }

  // Return existing promise if config is being fetched
  if (configPromise) {
    return configPromise;
  }

  // Fetch config from Rails backend API /v1/firebase_config
  // Prefer same-origin Next route which can securely fetch from backend and/or env.
  // This also avoids client-side auth/cookie issues when calling Rails directly.
  configPromise = axios.get('/config/firebase-config', { withCredentials: true })
    .then((response) => {
      const dbConfig = response.data;
      
      console.log('[Firebase Config] API response received:', dbConfig);
      console.log('[Firebase Config] Raw API response keys:', dbConfig ? Object.keys(dbConfig) : []);
      
      // Map response to FirebaseConfig format (handle multiple possible field name variations)
      const config: FirebaseConfig = {
        apiKey: dbConfig?.firebase_api_key || dbConfig?.apiKey || dbConfig?.firebase_apiKey || '',
        authDomain: dbConfig?.firebase_auth_domain || dbConfig?.authDomain || dbConfig?.firebase_authDomain || '',
        projectId: dbConfig?.firebase_project_id || dbConfig?.projectId || dbConfig?.firebase_projectId || '',
        storageBucket: dbConfig?.firebase_storage_bucket || dbConfig?.storageBucket || dbConfig?.firebase_storageBucket || '',
        messagingSenderId: dbConfig?.firebase_message_sender_id || 
                         dbConfig?.firebase_messaging_sender_id || 
                         dbConfig?.messagingSenderId || 
                         dbConfig?.firebase_messagingSenderId || '',
        appId: dbConfig?.firebase_app_id || dbConfig?.appId || dbConfig?.firebase_appId || '',
        measurementId: dbConfig?.firebase_measurement_id || dbConfig?.measurementId || dbConfig?.firebase_measurementId || '',
        vapidKey: dbConfig?.firebase_vapid_key || dbConfig?.vapidKey || dbConfig?.firebase_vapidKey || '',
      };
      
      console.log('[Firebase Config] Mapped config values:', {
        hasApiKey: !!config.apiKey,
        hasAuthDomain: !!config.authDomain,
        hasProjectId: !!config.projectId,
        hasStorageBucket: !!config.storageBucket,
        hasMessagingSenderId: !!config.messagingSenderId,
        hasAppId: !!config.appId,
        hasMeasurementId: !!config.measurementId,
        hasVapidKey: !!config.vapidKey,
      });
      
      // Check if we got valid required data
      const requiredFields = ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'messagingSenderId', 'appId'];
      const missingFields = requiredFields.filter((field) => !config[field as keyof typeof config]);
      
      if (missingFields.length === 0) {
        cachedConfig = config;
        console.log('[Firebase Config] ✅ Successfully loaded from database with all required fields');
        return config;
      } else {
        console.warn('[Firebase Config] Config incomplete - missing fields:', missingFields);
        // Fall through to environment variables (do not cache incomplete config)
        throw new Error('Incomplete config');
      }
    })
    .catch((error) => {
      console.warn('[Firebase Config] Error fetching from database, using environment variables:', error.message);
      
      // Fallback to environment variables
      const envConfig: FirebaseConfig = {
        apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY || '',
        authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN || '',
        projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID || '',
        storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET || '',
        messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID || '',
        appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID || '',
        measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID || '',
        vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY || '',
      };

      // Only cache env config if it's complete; otherwise allow retries later (e.g., after login).
      const requiredFields = ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'messagingSenderId', 'appId'];
      const missingFields = requiredFields.filter((field) => !envConfig[field as keyof typeof envConfig]);

      if (missingFields.length === 0) {
        cachedConfig = envConfig;
      } else {
        console.warn('[Firebase Config] Env config incomplete - missing fields:', missingFields);
      }

      return envConfig;
    })
    .finally(() => {
      configPromise = null;
    });

  return configPromise;
};

// Synchronous version for backward compatibility (returns cached config or empty)
const getFirebaseConfigSync = (): FirebaseConfig => {
  if (cachedConfig) {
    return cachedConfig;
  }
  // Return empty config if not yet loaded
  return {
    apiKey: '',
    authDomain: '',
    projectId: '',
    storageBucket: '',
    messagingSenderId: '',
    appId: '',
    vapidKey: '',
  };
};

let app: FirebaseApp | null = null;
let messaging: Messaging | null = null;
let analytics: Analytics | null = null;

// Initialize Firebase
export const initializeFirebase = async (): Promise<FirebaseApp | null> => {
  if (typeof window === 'undefined') {
    // Server-side: return null
    return null;
  }

  // Return existing app if already initialized
  const existingApps = getApps();
  if (existingApps.length > 0) {
    app = existingApps[0];
    return app;
  }

  // Wait a bit to ensure API has time to respond, then get config
  console.log('[Firebase] Waiting for Firebase config to load...');
  const config = await getFirebaseConfig();
  console.log('[Firebase] Config loaded, proceeding with initialization');

  // Check if config is valid (all required fields)
  const requiredFields = ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'messagingSenderId', 'appId'];
  const missingFields = requiredFields.filter((field) => !config[field as keyof typeof config]);
  
  if (missingFields.length > 0) {
    // Use warn (not error) so Next.js doesn't surface this as an app error overlay.
    console.warn('[Firebase] Configuration is incomplete. Missing fields:', missingFields);
    console.warn('[Firebase] Current config:', {
      hasApiKey: !!config.apiKey,
      hasAuthDomain: !!config.authDomain,
      hasProjectId: !!config.projectId,
      hasStorageBucket: !!config.storageBucket,
      hasMessagingSenderId: !!config.messagingSenderId,
      hasAppId: !!config.appId,
    });
    console.warn('[Firebase] Firebase initialization skipped. Push notifications will not work.');
    return null;
  }

  try {
    app = initializeApp(config);
    return app;
  } catch (error) {
    console.error('Firebase initialization error:', error);
    return null;
  }
};

// Get Firebase Messaging instance
export const getFirebaseMessaging = async (): Promise<Messaging | null> => {
  if (typeof window === 'undefined') {
    return null;
  }

  if (!app) {
    app = await initializeFirebase();
  }

  if (!app) {
    return null;
  }

  // Verify Firebase app is properly initialized
  const config = await getFirebaseConfig();
  if (!config.apiKey || !config.projectId) {
    console.warn('[Firebase] Firebase app is not properly configured:', {
      hasApiKey: !!config.apiKey,
      hasProjectId: !!config.projectId,
    });
    return null;
  }

  if (messaging) {
    return messaging;
  }

  try {
    // Check if browser supports messaging
    const hasServiceWorker = 'serviceWorker' in navigator;
    const hasPushManager = 'PushManager' in window;
    
    if (!hasServiceWorker) {
      console.warn('[Firebase] Service Worker not available in navigator');
    }
    if (!hasPushManager) {
      console.warn('[Firebase] PushManager not available in window');
    }

    if (hasServiceWorker && hasPushManager) {
      try {
        messaging = getMessaging(app);
        console.log('[Firebase] ✅ Messaging initialized successfully');
        console.log('[Firebase] Messaging instance:', messaging);
        return messaging;
      } catch (messagingError) {
        console.error('[Firebase] ❌ Error creating messaging instance:', messagingError);
        throw messagingError;
      }
    } else {
      const userAgent = typeof navigator !== 'undefined' && 'userAgent' in navigator ? navigator.userAgent : 'N/A';
      console.warn('[Firebase] Browser does not support push notifications', {
        hasServiceWorker,
        hasPushManager,
        userAgent: userAgent,
        protocol: window.location.protocol,
      });
      return null;
    }
  } catch (error) {
    console.error('[Firebase] ❌ Messaging initialization error:', error);
    console.error('[Firebase] Error details:', {
      error,
      app: !!app,
      hasWindow: typeof window !== 'undefined',
    });
    return null;
  }
};

// Get Firebase Analytics instance
export const getFirebaseAnalytics = async (): Promise<Analytics | null> => {
  if (typeof window === 'undefined') {
    return null;
  }

  if (!app) {
    app = await initializeFirebase();
  }

  if (!app) {
    return null;
  }

  if (analytics) {
    return analytics;
  }

  try {
    analytics = getAnalytics(app);
    return analytics;
  } catch (error) {
    console.error('Firebase Analytics initialization error:', error);
    return null;
  }
};

// Request notification permission
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (typeof window === 'undefined') {
    console.warn('[Firebase] Cannot request permission: window is undefined');
    return 'denied';
  }

  if (!('Notification' in window)) {
    console.warn('[Firebase] Notification API not available in this browser');
    return 'denied';
  }

  // Return existing permission if already granted or denied
  if (Notification.permission === 'granted') {
    console.log('[Firebase] Notification permission already granted');
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    console.warn('[Firebase] Notification permission already denied');
    return 'denied';
  }

  // Request permission (only works if permission is 'default')
  try {
    console.log('[Firebase] Requesting notification permission from user...');
    const permission = await Notification.requestPermission();
    console.log('[Firebase] Notification permission result:', permission);
    return permission;
  } catch (error) {
    console.error('[Firebase] Error requesting notification permission:', error);
    return 'denied';
  }
};

// Update Firebase token on backend
const updateFirebaseTokenOnBackend = async (fcmToken: string): Promise<void> => {
  try {
    const companyUserId = getCookie('company_user_id');
    const companyId = getCookie('company_id');

    if (!companyUserId || !companyId) {
      console.warn('[Firebase] Cannot update Firebase token: Missing user authentication data');
      return;
    }

    console.log('[Firebase] Updating Firebase token on backend...');

    // Call the API to update Firebase token
    await api.put('/v1/ca_user_profile/update_firebase_token', {
      p_logged_in_company_user_id: companyUserId,
      p_company_id: companyId,
      p_firebase_token: fcmToken,
    });

    console.log('[Firebase] ✅ Firebase token updated successfully on backend');
  } catch (error) {
    console.error('[Firebase] ❌ Failed to update Firebase token on backend:', error);
    // Don't throw - token generation was successful, backend update failure shouldn't block it
  }
};

// Wait for service worker to be ready and have Firebase config
const waitForServiceWorkerReady = async (maxWaitTime = 5000): Promise<boolean> => {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return false;
  }

  try {
    // Wait for service worker to be ready
    const registration = await navigator.serviceWorker.ready;
    
    if (!registration.active) {
      console.warn('[Firebase] Service worker is not active');
      return false;
    }

    // Get Firebase config and send it to service worker
    const config = await getFirebaseConfig();
    
    // Validate config has required fields
    if (!config.apiKey || !config.projectId) {
      console.warn('[Firebase] Firebase config is incomplete:', {
        hasApiKey: !!config.apiKey,
        hasProjectId: !!config.projectId,
      });
      return false;
    }
    
    // Send config to service worker
    registration.active.postMessage({
      type: 'FIREBASE_CONFIG',
      config: config,
    });
    
    console.log('[Firebase] Sent Firebase config to service worker, waiting for initialization...', {
      hasApiKey: !!config.apiKey,
      hasProjectId: !!config.projectId,
      hasVapidKey: !!config.vapidKey,
    });
    
    // Wait for service worker to process the config and initialize Firebase
    // The service worker initializes Firebase synchronously when it receives the config
    // We'll wait a reasonable amount of time for it to complete
    return new Promise((resolve) => {
      const startTime = Date.now();
      const checkInterval = 200; // Check every 200ms
      const maxWait = maxWaitTime;
      
      const checkReady = () => {
        const elapsed = Date.now() - startTime;
        
        if (elapsed >= maxWait) {
          console.warn('[Firebase] Service worker initialization timeout after', elapsed, 'ms');
          resolve(false);
          return;
        }
        
        // Send a ping to verify service worker is responsive
        // We'll assume it's ready after a reasonable delay
        if (elapsed >= 1000) {
          // Give service worker at least 1 second to initialize
          console.log('[Firebase] Service worker should be ready now (waited', elapsed, 'ms)');
          resolve(true);
          return;
        }
        
        // Check again after interval
        setTimeout(checkReady, checkInterval);
      };
      
      // Start checking after initial delay
      setTimeout(checkReady, 500);
    });
  } catch (error) {
    console.error('[Firebase] Error waiting for service worker:', error);
    return false;
  }
};

// Get FCM token
export const getFCMToken = async (): Promise<string | null> => {
  const messagingInstance = await getFirebaseMessaging();
  
  if (!messagingInstance) {
    console.warn('[Firebase] Messaging is not available');
    return null;
  }

  try {
    const permission = await requestNotificationPermission();
    
    if (permission !== 'granted') {
      console.warn('[Firebase] Notification permission not granted');
      return null;
    }

    // Ensure service worker is ready and has Firebase config before requesting token
    console.log('[Firebase] Ensuring service worker is ready...');
    const serviceWorkerReady = await waitForServiceWorkerReady();
    
    if (!serviceWorkerReady) {
      console.warn('[Firebase] Service worker may not be ready, but proceeding with token request...');
    }

    console.log('[Firebase] Requesting FCM token...');

    // Get VAPID key from config
    const config = await getFirebaseConfig();
    const vapidKey = config.vapidKey;

    // Request token with VAPID key if available
    const tokenOptions: { vapidKey?: string } = {};
    if (vapidKey) {
      tokenOptions.vapidKey = vapidKey;
      console.log('[Firebase] Using VAPID key for token generation');
    } else {
      console.warn('[Firebase] ⚠️ VAPID key not found. Push notifications from Firebase Console may not work.');
      console.warn('[Firebase] Please set FIREBASE_VAPID_KEY environment variable.');
    }

    // Retry logic for token request (in case service worker needs more time)
    let token: string | null = null;
    let retries = 0;
    const maxRetries = 3;
    
    while (!token && retries < maxRetries) {
      try {
        token = await getToken(messagingInstance, tokenOptions);
        if (token) {
          break;
        }
      } catch (tokenError: any) {
        retries++;
        const errorMessage = tokenError?.message || String(tokenError);
        
        // Check if it's an authentication error
        if (errorMessage.includes('authentication credential') || 
            errorMessage.includes('token-subscribe-failed')) {
          console.warn(`[Firebase] Authentication error (attempt ${retries}/${maxRetries}):`, errorMessage);
          
          if (retries < maxRetries) {
            // Wait longer and ensure service worker has config
            console.log('[Firebase] Waiting for service worker to initialize Firebase...');
            await waitForServiceWorkerReady(3000);
            await new Promise(resolve => setTimeout(resolve, 1000 * retries)); // Exponential backoff
            continue;
          } else {
            throw tokenError;
          }
        } else {
          // Other errors, throw immediately
          throw tokenError;
        }
      }
    }

    if (token) {
      console.log('[Firebase] ✅ FCM registration token obtained successfully');
      
      // Update Firebase token on the backend
      await updateFirebaseTokenOnBackend(token);
      
      // Log that push notifications are enabled
      try {
        const { logPushNotificationEnabled } = await import('./notificationLogger');
        await logPushNotificationEnabled(token);
      } catch (logError) {
        console.error('[Firebase] Failed to log push notification enabled:', logError);
      }
      
      return token;
    } else {
      console.warn('[Firebase] No registration token available');
      return null;
    }
  } catch (error) {
    console.error('[Firebase] ❌ Error getting FCM token:', error);
    
    // Provide helpful error messages
    if (error instanceof Error) {
      const errorMessage = error.message || String(error);
      console.error('[Firebase] Error details:', {
        message: errorMessage,
        name: error.name,
      });

      console.error('[Firebase] This might be a Firebase configuration issue');
      console.error('[Firebase] Make sure all required Firebase config values are set correctly');
    } else {
      console.error('[Firebase] Unknown error type:', error);
    }
    
    // Don't throw - return null to prevent unhandled errors
    return null;
  }
};

// Listen for foreground messages
export const onForegroundMessage = async (
  callback: (payload: MessagePayload) => void
): Promise<(() => void) | null> => {
  console.log('[Firebase] Setting up foreground message listener...');
  
  // Ensure Firebase app is initialized first
  if (!app) {
    console.log('[Firebase] Firebase app not initialized, initializing now...');
    await initializeFirebase();
  }
  
  const messagingInstance = await getFirebaseMessaging();
  
  if (!messagingInstance) {
    console.error('[Firebase] ❌ Cannot setup foreground listener: Messaging instance not available');
    console.error('[Firebase] Debug info:', {
      hasWindow: typeof window !== 'undefined',
      hasApp: !!app,
      hasMessaging: !!messaging,
      hasServiceWorker: typeof navigator !== 'undefined' && 'serviceWorker' in navigator,
      hasPushManager: typeof window !== 'undefined' && 'PushManager' in window,
    });
    return null;
  }

  console.log('[Firebase] ✅ Messaging instance available, setting up onMessage listener...');
  console.log('[Firebase] Messaging instance:', messagingInstance);

  try {
    const unsubscribe = onMessage(messagingInstance, async (payload) => {
      console.log('[Firebase] 📬 FOREGROUND message received:', payload);
      console.log('[Firebase] Payload structure:', {
        hasNotification: !!payload.notification,
        hasData: !!payload.data,
        notification: payload.notification,
        data: payload.data,
        messageId: payload.messageId,
        from: (payload as any).from,
      });
      
      // Verify message format
      if (!payload.notification && !payload.data) {
        console.error('[Firebase] ⚠️ Message has no notification or data payload');
        console.error('[Firebase] Full payload:', JSON.stringify(payload, null, 2));
      }

      const title = payload.notification?.title || payload.data?.title || 'New Notification';
      const body = payload.notification?.body || payload.data?.body || '';
      const messageId = payload.messageId || 'unknown';
      const icon = payload.notification?.icon || payload.data?.icon;

      // Track push notification toasts IMMEDIATELY - BEFORE any API calls to prevent dismissal
      // This must happen FIRST, before any async operations that might trigger API calls
      if (typeof window !== 'undefined') {
        if (!(window as any).__pushNotificationToastIds) {
          (window as any).__pushNotificationToastIds = new Set<string>();
        }
        // Track that a push notification toast will be active (use messageId as identifier)
        (window as any).__pushNotificationToastIds.add(messageId);
        console.log('[Firebase] Tracked push notification toast (foreground, IMMEDIATE):', messageId);
      }

      // Track displayed notifications to prevent duplicates
      if (typeof window !== 'undefined') {
        if (!(window as any).__displayedNotificationIds) {
          (window as any).__displayedNotificationIds = new Set<string>();
        }
        
        // Check if this notification was already displayed (prevent duplicates)
        if ((window as any).__displayedNotificationIds.has(messageId)) {
          console.log('[Firebase] Skipping toast - foreground notification already displayed:', messageId);
          // Still log the notification (but delay it to avoid dismissing toast)
          setTimeout(async () => {
            try {
              const { logNotification } = await import('./notificationLogger');
              await logNotification({
                title,
                body,
                messageId,
                data: payload.data,
                receivedAt: new Date(),
                notificationType: 'foreground',
              });
            } catch (logError) {
              console.error('[Firebase] Failed to log foreground notification:', logError);
            }
          }, 500); // Delay logging to ensure toast is displayed first
          return;
        }
        
        // Mark this notification as displayed
        (window as any).__displayedNotificationIds.add(messageId);
        
        // Clean up old IDs after 1 hour to prevent memory leaks
        setTimeout(() => {
          if ((window as any).__displayedNotificationIds) {
            (window as any).__displayedNotificationIds.delete(messageId);
          }
        }, 60 * 60 * 1000); // 1 hour
      }

      // Display toast notification for FOREGROUND messages
      // Use requestAnimationFrame to ensure DOM is ready, then setTimeout for ToastContainer
      if (typeof window !== 'undefined') {
        // Try immediate display first (no delays)
        try {
          const notificationUrl = payload.data?.url || payload.data?.click_action || payload.fcmOptions?.link || '/';
          
          // Verify tracking
          if (!(window as any).__pushNotificationToastIds) {
            (window as any).__pushNotificationToastIds = new Set<string>();
          }
          if (!(window as any).__pushNotificationToastIds.has(messageId)) {
            (window as any).__pushNotificationToastIds.add(messageId);
          }
          
          // Check if toast is available
          if (typeof toast === 'undefined' || !toast.info) {
            console.error('[Firebase] ❌ Toast is not available. Make sure ToastContainer is rendered in the app.');
            if ((window as any).__pushNotificationToastIds) {
              (window as any).__pushNotificationToastIds.delete(messageId);
            }
            return;
          }
          
          console.log('[Firebase] 🍞 Displaying toast IMMEDIATELY (no delay)...', {
            title,
            body,
            messageId,
          });
          
          // Display toast immediately - no requestAnimationFrame or setTimeout
          const toastMessage = `${title}${body ? ': ' + body : ''}`;
          const toastId = toast.info(toastMessage, {
                position: 'top-right' as const,
                autoClose: false,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                toastId: `push-${messageId}`,
                onClose: () => {
                  console.log('[Firebase] Toast closed (foreground):', messageId);
                  if ((window as any).__pushNotificationToastIds) {
                    (window as any).__pushNotificationToastIds.delete(messageId);
                  }
                },
                onClick: () => {
                  console.log('[Firebase] Toast clicked (foreground), navigating to:', notificationUrl);
                  window.location.href = notificationUrl;
                },
          });
          
          console.log('[Firebase] ✅ Toast.info called, returned:', toastId);
          
          // Verify toast appears
          setTimeout(() => {
            const toasts = document.querySelectorAll('.Toastify__toast');
            const container = document.querySelector('.Toastify__toast-container');
            console.log('[Firebase] Toast verification:', {
              toastCount: toasts.length,
              hasContainer: !!container,
              toastId,
              found: Array.from(toasts).some((el: any) => 
                el.id === toastId || el.id?.includes(toastId) || el.getAttribute('data-toast-id') === toastId
              ),
            });
            
            if (toasts.length === 0 && container) {
              console.warn('[Firebase] ⚠️ Toast not found, trying delayed fallback...');
              setTimeout(() => {
                toast.info(toastMessage, {
                  position: 'top-right' as const,
                  autoClose: false,
                  toastId: `push-${messageId}-fallback-${Date.now()}`,
                });
              }, 300);
            }
          }, 100);
        } catch (toastError) {
          console.error('[Firebase] ❌ Error displaying toast:', toastError);
          // Fallback: try with delay
          setTimeout(() => {
            try {
              const toastMessage = `${title}${body ? ': ' + body : ''}`;
              toast.info(toastMessage, {
                position: 'top-right' as const,
                autoClose: false,
                toastId: `push-${messageId}-error-fallback-${Date.now()}`,
              });
            } catch (fallbackError) {
              console.error('[Firebase] ❌ Fallback toast also failed:', fallbackError);
            }
          }, 200);
          
          if ((window as any).__pushNotificationToastIds) {
            (window as any).__pushNotificationToastIds.delete(messageId);
          }
        }
      }

      // Log the notification AFTER a delay to ensure toast is displayed first
      // This prevents the API interceptor from dismissing the toast
      setTimeout(async () => {
        try {
          const { logNotification } = await import('./notificationLogger');
          await logNotification({
            title,
            body,
            messageId,
            data: payload.data,
            receivedAt: new Date(),
            notificationType: 'foreground',
          });
        } catch (logError) {
          console.error('[Firebase] Failed to log foreground notification:', logError);
        }
      }, 500); // Delay logging to ensure toast is displayed first

      // Display browser notification if permission is granted
      if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
        try {
          const notificationOptions: NotificationOptions = {
            body: body,
            icon: payload.notification?.icon || payload.data?.icon || '/icons/notification-icon.png',
            badge: '/icons/badge-icon.png',
            tag: payload.data?.tag || messageId,
            data: {
              ...payload.data,
              url: payload.data?.url || payload.fcmOptions?.link || '/',
              click_action: payload.data?.click_action || payload.fcmOptions?.link || '/',
            },
            requireInteraction: typeof payload.data?.requireInteraction === 'boolean' 
              ? payload.data.requireInteraction 
              : String(payload.data?.requireInteraction) === 'true',
          };

          // Show notification
          const notification = new Notification(title, notificationOptions);
          
          // Store URL for click handler
          const notificationUrl = payload.data?.url || payload.data?.click_action || payload.fcmOptions?.link || '/';
          
          // Handle notification click
          notification.onclick = (event) => {
            event.preventDefault();
            
            // Log click
            import('./notificationLogger').then(({ logNotificationClick }) => {
              logNotificationClick({
                title,
                url: notificationUrl,
                messageId,
              }).catch(err => console.error('[Firebase] Failed to log notification click:', err));
            });

            // Navigate to URL
            window.focus();
            window.location.href = notificationUrl;
            
            notification.close();
          };

          // Auto-close after 5 seconds if not clicked
          setTimeout(() => {
            notification.close();
          }, 5000);

          console.log('[Firebase] ✅ Browser notification displayed for foreground message');
        } catch (notificationError) {
          console.error('[Firebase] ❌ Failed to display browser notification:', notificationError);
        }
      } else {
        console.log('[Firebase] Cannot display browser notification: Permission not granted or Notification API not available');
      }
      
      // Call the user's callback
      try {
        callback(payload);
      } catch (callbackError) {
        console.error('[Firebase] Error in foreground message callback:', callbackError);
      }
    });
    
    console.log('[Firebase] ✅ Foreground message listener set up successfully');
    return unsubscribe;
  } catch (error) {
    console.error('[Firebase] ❌ Error setting up foreground message listener:', error);
    return null;
  }
};

export default {
  initializeFirebase,
  getFirebaseMessaging,
  getFirebaseAnalytics,
  requestNotificationPermission,
  getFCMToken,
  onForegroundMessage,
};

